import { useEffect, useRef, useState } from 'react'

import {
  type LibraryDocument,
  type LibraryFolder,
  canBeWrittenIn,
  fetchText,
  previewUrl,
} from '../../api/library'
import { Preview } from '../../ui/preview/Preview'

/**
 * How the library shows what it holds.
 *
 * One rule decides almost everything here: **a document appears as its content**, not as an icon with
 * a file name beside it. The first page of the script, the photograph itself. That single difference is
 * most of the distance between MyLib and the file lists it is meant not to feel like, so the picture is
 * the tile and the words sit under it, quietly.
 *
 * Where there is no picture — a spreadsheet, an audio file, a PDF nothing could be read from — the tile
 * says what the thing *is*, in type, at a size worth reading. That is still showing rather than
 * labelling, and it is never a grey sheet-of-paper icon.
 */

/**
 * What a dragged document puts on the clipboard, so that a drop can tell it from a dropped file.
 *
 * It has to be *something*: a drag that carries no data of its own is filled in by the browser from
 * whatever it finds inside the element — and inside a tile it finds the preview image, which then
 * arrives at the shelf's file drop as a file called `preview.jpg` and is uploaded as a new document.
 * Naming the payload is what makes a move a move.
 */
export const DRAGGED_DOCUMENT = 'application/x-mylib-document'

/** Whether this drag is files from outside, rather than a document being moved inside the page. */
export function carriesFiles(data: DataTransfer): boolean {
  return data.types.includes('Files') && !data.types.includes(DRAGGED_DOCUMENT)
}

/** How big a file is, in the unit somebody would say it in. */
export function asSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} bytes`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  const megabytes = bytes / (1024 * 1024)
  return megabytes >= 10 ? `${Math.round(megabytes)} MB` : `${megabytes.toFixed(1)} MB`
}

/** A date somebody can read, in their own locale. The time of day is not what a library is about. */
export function asDate(iso: string): string {
  const at = new Date(iso)
  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export interface DocumentTileProps {
  readonly document: LibraryDocument
  readonly open: boolean
  readonly onOpen: () => void
  /** Called while this tile is being dragged, so a folder can offer itself as a destination. */
  readonly onDragStart: () => void
  readonly onDragEnd: () => void
}

export function DocumentTile({
  document,
  open,
  onOpen,
  onDragStart,
  onDragEnd,
}: DocumentTileProps) {
  return (
    <li
      className="tile"
      draggable
      onDragStart={(event) => {
        // The id, so the folder that takes it knows what it is taking, and a move rather than a copy.
        event.dataTransfer.setData(DRAGGED_DOCUMENT, document.id)
        event.dataTransfer.setData('text/plain', document.title)
        event.dataTransfer.effectAllowed = 'move'
        onDragStart()
      }}
      onDragEnd={onDragEnd}
    >
      <button
        type="button"
        className="tile__open"
        aria-current={open ? 'true' : undefined}
        onClick={onOpen}
      >
        <span className="tile__picture">
          {/*
            Keyed by the content, so a document that was edited asks for its picture again. The
            attempt is given up when one comes back empty — a written document has none until it has
            been typed into — and without this the tile would keep the "no picture" it learned then
            for as long as the shelf stays on the screen.
          */}
          <Picture key={document.contentId} document={document} />
        </span>
        <span className="tile__name">{document.title}</span>
        <span className="tile__facts">
          <span className="tile__kind">{document.kind === '' ? 'File' : document.kind}</span>
          <span aria-hidden="true">·</span>
          <span>{asSize(document.sizeInBytes)}</span>
          {document.hasEarlierVersions && (
            <>
              <span aria-hidden="true">·</span>
              <span>v{document.version}</span>
            </>
          )}
        </span>
        {/*
          Said on the tile, because that is where it is seen first — and said with the name of the
          system, since "no longer available" without saying where means nothing. The document is
          untouched: it opens, it downloads, and it keeps everything that hangs off it.
        */}
        {document.goneFromSource && <span className="tile__gone">{goneFrom(document)}</span>}
      </button>
    </li>
  )
}

/** Which source a document has disappeared from, named — the first one that says so. */
export function goneFrom(document: LibraryDocument): string {
  const gone = document.origins.find((origin) => origin.goneFromSourceAt !== null)
  return gone === undefined || gone.source === ''
    ? 'No longer in its source'
    : `No longer in ${gone.source}`
}

/**
 * The picture of a document's content, or something worth reading instead.
 *
 * The service says whether a picture can be made at all (`hasPreview`), and the image itself says
 * whether one actually could: a PDF with a broken first page answers with a `404`, and `onError` is
 * what turns that into the same fallback. Lazy, because a folder can hold thirty of these and a phone
 * should not fetch the ones below the fold.
 */
export function Picture({ document }: { readonly document: LibraryDocument }) {
  const [drawn, setDrawn] = useState(document.hasPreview)

  if (!drawn) {
    /*
      A written document draws itself, here in the browser.

      Everything else on a shelf shows its content — a photo is the photo, a PDF is its first page —
      and a Markdown file showed the letters `MD` on grey. It is the one kind of document whose
      content the browser can already draw perfectly well: the renderer is the same one the editor
      previews with, and it costs no container and no configuration. The typesetter makes a *page* of
      a written document where one is installed; this is what a shelf shows when none is.
    */
    if (canBeWrittenIn(document)) {
      return <WrittenPicture document={document} />
    }
    return (
      <span className="tile__instead" aria-hidden="true">
        {document.kind === '' ? 'FILE' : document.kind}
      </span>
    )
  }

  return (
    <img
      className="tile__image"
      src={previewUrl(document)}
      alt=""
      loading="lazy"
      decoding="async"
      // Not draggable itself. Otherwise dragging the tile drags the *picture*, and the picture arrives
      // at the shelf's file drop as a file to be uploaded.
      draggable={false}
      onError={() => {
        setDrawn(false)
      }}
    />
  )
}

/**
 * A written document's first lines, drawn where its picture would be.
 *
 * **The text is fetched only once the tile is actually on the screen.** A folder holds thirty of
 * these, and a shelf that asked for all thirty on arrival would be a shelf that takes a second to
 * open on a phone — the same reason the images above say `loading="lazy"`, done by hand because
 * there is no such attribute for a piece of text.
 *
 * What is drawn is the beginning and never the whole file: a tile is a glance, the document opens
 * when more is wanted, and rendering a hundred kilobytes of Markdown into a box six lines tall is
 * work nobody sees. It is inert — nothing in it is a control — because it is a picture of the
 * content and the tile around it is what answers a press.
 */
function WrittenPicture({ document: written }: { readonly document: LibraryDocument }) {
  const [text, setText] = useState<string | null>(null)
  const [nothing, setNothing] = useState(false)
  const box = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const element = box.current
    if (element === null) {
      return
    }

    const controller = new AbortController()
    const read = () => {
      void fetchText(written.id, undefined, controller.signal)
        .then((answer) => {
          if (!controller.signal.aborted) {
            setText(answer.text.slice(0, MOST_SHOWN))
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setNothing(true)
          }
        })
    }

    // Where there is no observer the text is simply read straight away, which is the behaviour this
    // replaced rather than a worse one.
    if (typeof IntersectionObserver !== 'function') {
      read()
      return () => {
        controller.abort()
      }
    }

    const watching = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        watching.disconnect()
        read()
      }
    })
    watching.observe(element)
    return () => {
      watching.disconnect()
      controller.abort()
    }
  }, [written.id])

  if (nothing) {
    return (
      <span className="tile__instead" aria-hidden="true">
        {written.kind === '' ? 'FILE' : written.kind}
      </span>
    )
  }

  return (
    <span className="tile__written" ref={box} aria-hidden="true">
      {text !== null && <Preview text={text} format={written.format ?? ''} label="" />}
    </span>
  )
}

/** How much of a written document a tile draws. A glance, not the file. */
const MOST_SHOWN = 2_000

export interface FolderTileProps {
  readonly folder: LibraryFolder
  readonly onOpen: () => void
  /** Set while something is being dragged, so the tile can show that it would take it. */
  readonly offering: boolean
  /** Takes the document that was dropped on it. The id comes off the drag, not out of a state. */
  readonly onDrop: (document: string) => void
}

/**
 * A folder, and while something is being dragged, a place to put it.
 *
 * Dragging is the quicker way and never the only one: the same move is a visible control on the open
 * document, because a drag cannot be discovered with a mouse, cannot be explained in a sentence, and
 * does not exist on a touch screen at all.
 */
export function FolderTile({ folder, onOpen, offering, onDrop }: FolderTileProps) {
  const [over, setOver] = useState(false)

  return (
    <li
      className={offering && over ? 'tile tile--folder tile--taking' : 'tile tile--folder'}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes(DRAGGED_DOCUMENT)) {
          event.preventDefault()
          event.dataTransfer.dropEffect = 'move'
          setOver(true)
        }
      }}
      onDragLeave={() => {
        setOver(false)
      }}
      onDrop={(event) => {
        const moved = event.dataTransfer.getData(DRAGGED_DOCUMENT)
        if (moved !== '') {
          event.preventDefault()
          setOver(false)
          onDrop(moved)
        }
      }}
    >
      <button type="button" className="tile__open" onClick={onOpen}>
        <span className="tile__picture tile__picture--folder" aria-hidden="true">
          <FolderShape />
        </span>
        <span className="tile__name">{folder.name}</span>
        <span className="tile__facts">
          {folder.documents === 0
            ? 'Empty'
            : `${folder.documents} document${folder.documents === 1 ? '' : 's'}`}
        </span>
      </button>
    </li>
  )
}

/** The one folder drawing, so a folder looks like a folder wherever one is shown. */
export function FolderShape() {
  return (
    <svg viewBox="0 0 48 36" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h11l4 4h22A2.5 2.5 0 0 1 45 12.5v18a2.5 2.5 0 0 1-2.5 2.5h-37A2.5 2.5 0 0 1 3 30.5z" />
    </svg>
  )
}

export interface NamingTileProps {
  /** The name it starts with. Empty for a folder that does not exist yet. */
  readonly initial: string
  readonly onDone: (name: string) => void
  readonly onGiveUp: () => void
}

/**
 * A folder being named, in the place the folder will be.
 *
 * This is what "New folder" produces, and it is deliberately *not* a form: no label beside the shelf,
 * no Done, no Cancel. The tile appears where the folder is going to sit, the name is typed onto it, and
 * Enter — or simply clicking elsewhere — keeps it. Escape, or leaving it empty, drops it again.
 *
 * It is also the reason nothing here has to be aligned against anything: the name sits where the name
 * of a folder sits, in the same tile, in the same type.
 */
export function NamingTile({ initial, onDone, onGiveUp }: NamingTileProps) {
  const [name, setName] = useState(initial)
  // Escape unmounts the tile, which blurs the field — and a blur keeps what was typed. Without this,
  // giving up would create the folder anyway.
  const [settled, setSettled] = useState(false)

  const keep = () => {
    if (settled) {
      return
    }
    setSettled(true)
    const wanted = name.trim()
    if (wanted === '') {
      onGiveUp()
    } else {
      onDone(wanted)
    }
  }

  return (
    <li className="tile tile--folder tile--naming">
      <span className="tile__picture tile__picture--folder" aria-hidden="true">
        <FolderShape />
      </span>
      <input
        className="tile__field"
        // Focused on appearing: a control was just used to ask for exactly this field.
        autoFocus
        aria-label="Name of the new folder"
        placeholder="Folder name"
        value={name}
        onChange={(event) => {
          setName(event.target.value)
        }}
        onBlur={keep}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            keep()
          }
          if (event.key === 'Escape') {
            setSettled(true)
            onGiveUp()
          }
        }}
      />
      <span className="tile__facts">Enter keeps it, Escape drops it</span>
    </li>
  )
}

export interface CountedTileProps {
  readonly count: number
  readonly onOpen: () => void
}

/**
 * The way into the trash, shown only while there is something in it.
 *
 * Beside the folders rather than tucked into a menu: what somebody deleted is something they may well
 * want back, and a way back that has to be looked for is a way back nobody finds in a hurry.
 */
export function TrashTile({ count, onOpen }: CountedTileProps) {
  return (
    <li className="tile tile--folder tile--trash">
      <button type="button" className="tile__open" onClick={onOpen}>
        <span className="tile__picture tile__picture--folder" aria-hidden="true">
          <svg viewBox="0 0 48 36" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M10 10h28l-2.5 22a2 2 0 0 1-2 1.8H14.5a2 2 0 0 1-2-1.8z" />
            <path d="M7 10h34M19 7h10M20 17v11M28 17v11" />
          </svg>
        </span>
        <span className="tile__name">Trash</span>
        <span className="tile__facts">{count === 1 ? '1 thing' : `${count} things`}</span>
      </button>
    </li>
  )
}
